import log from "logbeacon";
import type { StartTrackingArgs } from "../types";

const TRIGGER_TYPES = new Set<StartTrackingArgs["trigger_type"]>([
  "cron",
  "once",
  "event_trigger",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseNextTriggered(raw: unknown): StartTrackingArgs["next_triggered"] {
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) {
    return raw;
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) {
      return undefined;
    }
    const asNumber = Number(trimmed);
    if (Number.isFinite(asNumber) && asNumber > 0) {
      return asNumber;
    }
    return trimmed;
  }
  return undefined;
}

/**
 * 解析 start_tracking interactive tool args。
 * @see fe-spec-edg-118 §2.1
 */
export function parseStartTrackingArgs(raw: unknown): StartTrackingArgs | null {
  if (!isRecord(raw)) {
    return null;
  }

  const trackerId =
    typeof raw.tracker_id === "string" ? raw.tracker_id.trim() : "";
  const title = typeof raw.title === "string" ? raw.title.trim() : "";

  if (!trackerId || !title) {
    if (process.env.NODE_ENV !== "production") {
      log.warn("[parseStartTrackingArgs] missing tracker_id or title", raw);
    }
    return null;
  }

  const triggerType = raw.trigger_type;
  const normalizedTrigger: StartTrackingArgs["trigger_type"] =
    typeof triggerType === "string" &&
    TRIGGER_TYPES.has(triggerType as StartTrackingArgs["trigger_type"])
      ? (triggerType as StartTrackingArgs["trigger_type"])
      : "event_trigger";

  return {
    tracker_id: trackerId,
    title,
    description:
      typeof raw.description === "string" ? raw.description : undefined,
    trigger_type: normalizedTrigger,
    schedule_cron:
      typeof raw.schedule_cron === "string" ? raw.schedule_cron : undefined,
    next_triggered: parseNextTriggered(raw.next_triggered),
    condition_logic:
      raw.condition_logic === "AND" || raw.condition_logic === "OR"
        ? raw.condition_logic
        : undefined,
    conditions: Array.isArray(raw.conditions)
      ? (raw.conditions as StartTrackingArgs["conditions"])
      : undefined,
    domain:
      raw.domain === "FINANCE" ||
      raw.domain === "INVEST" ||
      raw.domain === "OTHER"
        ? raw.domain
        : undefined,
    task_description:
      typeof raw.task_description === "string"
        ? raw.task_description
        : undefined,
    payload: typeof raw.payload === "string" ? raw.payload : undefined,
  };
}
